/**
 * Admin Operations dashboard (Phase 3.5):
 * - Deliveries Calendar (list-by-day view)
 * - Stock Report (table + CSV export)
 *
 * Both use a shared date-range picker at the top.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Calendar, Download, Loader2 } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { getToken } from '@/lib/adminApi';

const API = process.env.REACT_APP_BACKEND_URL || '';

function isoToday() { return new Date().toISOString().slice(0, 10); }
function isoPlus(days) {
  const d = new Date();
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
}

async function adminGet(path) {
  const t = getToken();
  const res = await fetch(`${API}${path}`, {
    headers: { Authorization: t ? `Bearer ${t}` : '' },
  });
  if (!res.ok) throw new Error((await res.text()) || `${res.status}`);
  return res.json();
}

export default function AdminOperationsPage() {
  const [from, setFrom] = useState(isoToday());
  const [to, setTo] = useState(isoPlus(30));
  const [tab, setTab] = useState('calendar');
  const [calendar, setCalendar] = useState(null);
  const [stock, setStock] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const load = async () => {
    setLoading(true); setError('');
    try {
      const [cal, st] = await Promise.all([
        adminGet(`/api/admin/deliveries-calendar?from=${from}&to=${to}`),
        adminGet(`/api/admin/stock-report?from=${from}&to=${to}`),
      ]);
      setCalendar(cal); setStock(st);
    } catch (e) {
      setError(e.message || 'Failed to load');
    } finally { setLoading(false); }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  const downloadCSV = () => {
    const t = getToken();
    const url = `${API}/api/admin/stock-report?fmt=csv&from=${from}&to=${to}`;
    // Use fetch + blob so we can attach the bearer header.
    fetch(url, { headers: { Authorization: t ? `Bearer ${t}` : '' } })
      .then((r) => r.blob())
      .then((blob) => {
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        a.download = `stock_report_${from}_${to}.csv`;
        a.click();
        URL.revokeObjectURL(a.href);
      });
  };

  const dayList = useMemo(() => {
    if (!calendar) return [];
    return Object.entries(calendar.by_day || {}).map(([day, events]) => ({ day, events }));
  }, [calendar]);

  return (
    <div className="space-y-5" data-testid="admin-operations-page">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-3xl font-bold text-[#05223D]">Operations</h1>
          <p className="text-sm text-[#465B70] mt-1">
            Deliveries calendar and product-level stock plan.
          </p>
        </div>
      </div>

      {/* Date range filters */}
      <Card>
        <CardContent className="flex flex-wrap items-end gap-3 p-4">
          <div>
            <Label className="text-xs uppercase tracking-wider">From</Label>
            <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="h-10 rounded-xl" data-testid="ops-from-date" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider">To</Label>
            <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="h-10 rounded-xl" data-testid="ops-to-date" />
          </div>
          <Button onClick={load} disabled={loading} className="h-10 rounded-xl bg-[#0A4D8C]" data-testid="ops-reload">
            {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
            Refresh
          </Button>
        </CardContent>
      </Card>

      {error && (
        <div className="rounded-xl border border-[#9b1c1c]/30 bg-[#9b1c1c]/8 px-3 py-2 text-sm text-[#9b1c1c]">
          {error}
        </div>
      )}

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="calendar" data-testid="ops-tab-calendar">
            Calendar {calendar && <Badge variant="secondary" className="ml-2 rounded-full">{calendar.total}</Badge>}
          </TabsTrigger>
          <TabsTrigger value="stock" data-testid="ops-tab-stock">
            Stock report {stock && <Badge variant="secondary" className="ml-2 rounded-full">{stock.rows.length}</Badge>}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="calendar" className="mt-4">
          <Card>
            <CardContent className="p-0">
              {!calendar || dayList.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-16 text-[#465B70]">
                  <Calendar className="h-8 w-8 mb-2" />
                  No deliveries scheduled in this range.
                </div>
              ) : (
                <ul className="divide-y">
                  {dayList.map(({ day, events }) => (
                    <li key={day} className="p-4" data-testid={`ops-cal-day-${day}`}>
                      <p className="mb-2 text-xs uppercase tracking-wider font-bold text-[#F25C05]">{day}</p>
                      <ul className="space-y-2">
                        {events.map((e, i) => (
                          <li key={i} className="flex items-center justify-between text-sm">
                            <div>
                              <span className="font-bold text-[#05223D]">{e.product_name}</span>
                              <span className="text-[#465B70]"> · {e.qty} pcs · {e.customer_name || '—'} · </span>
                              <span className="text-[#0A4D8C]">{e.subscription_label}</span>
                            </div>
                            <Badge variant="secondary" className="rounded-full">{e.frequency}</Badge>
                          </li>
                        ))}
                      </ul>
                    </li>
                  ))}
                </ul>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="stock" className="mt-4">
          <Card>
            <CardContent className="p-0">
              <div className="flex items-center justify-between px-4 py-3 border-b">
                <p className="text-sm font-bold text-[#05223D]">
                  Total qty in range: {stock?.total?.toFixed?.(2) || 0}
                </p>
                <Button onClick={downloadCSV} variant="outline" size="sm" className="rounded-xl" data-testid="ops-download-csv">
                  <Download className="mr-1.5 h-4 w-4" /> Export CSV
                </Button>
              </div>
              {!stock || stock.rows.length === 0 ? (
                <div className="py-16 text-center text-[#465B70]">
                  Nothing to plan for this date range.
                </div>
              ) : (
                <table className="w-full text-sm">
                  <thead className="bg-[#F5F2EB] text-[#465B70]">
                    <tr>
                      <th className="text-left px-4 py-2">Product</th>
                      <th className="text-right px-4 py-2">Total qty</th>
                      <th className="text-right px-4 py-2">Orders</th>
                      <th className="text-left px-4 py-2">First date</th>
                      <th className="text-left px-4 py-2">Last date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {stock.rows.map((r, i) => (
                      <tr key={i} className="border-t" data-testid={`ops-stock-row-${i}`}>
                        <td className="px-4 py-2 font-bold text-[#05223D]">{r.product_name || '—'}</td>
                        <td className="px-4 py-2 text-right tabular-nums">{r.total_qty.toFixed(2)}</td>
                        <td className="px-4 py-2 text-right tabular-nums">{r.orders}</td>
                        <td className="px-4 py-2 text-[#465B70]">{r.earliest?.slice(0, 10)}</td>
                        <td className="px-4 py-2 text-[#465B70]">{r.latest?.slice(0, 10)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
